import { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { useNarration } from '../audio/NarrationContext'
import { STORY, NARRATIONS } from '../config'

const RATES = [1, 1.25, 1.5, 0.75]

function fmt(s) {
  if (!s || !isFinite(s)) return '0:00'
  const m = Math.floor(s / 60)
  const sec = Math.floor(s % 60)
  return `${m}:${String(sec).padStart(2, '0')}`
}

function NarrationPlayer() {
  const { stop, current, playing, getAudio } = useNarration()
  const active = current === 'story' && playing
  const [time, setTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [rate, setRate] = useState(1)
  const [missing, setMissing] = useState(false)
  const [showTranscript, setShowTranscript] = useState(false)

  useEffect(() => {
    const el = getAudio()
    const onTime = () => setTime(el.currentTime)
    const onMeta = () => setDuration(el.duration)
    const onErr = () => {
      if (current === 'story') setMissing(true)
    }
    el.addEventListener('timeupdate', onTime)
    el.addEventListener('loadedmetadata', onMeta)
    el.addEventListener('error', onErr)
    return () => {
      el.removeEventListener('timeupdate', onTime)
      el.removeEventListener('loadedmetadata', onMeta)
      el.removeEventListener('error', onErr)
    }
  }, [getAudio, current])

  const seek = (e) => {
    const el = getAudio()
    if (!duration) return
    el.currentTime = Number(e.target.value)
    setTime(el.currentTime)
  }

  const cycleRate = () => {
    const next = RATES[(RATES.indexOf(rate) + 1) % RATES.length]
    setRate(next)
    getAudio().playbackRate = next
  }

  if (missing) {
    return (
      <div
        data-testid="audio-player-missing"
        className="mt-14 rounded-2xl border border-[var(--story-amber)]/30 bg-[var(--story-amber)]/5 p-5 text-center"
      >
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--story-amber)]/70">
          My story, in my voice
        </p>
        <p className="font-serif-vintage mt-3 text-sm italic text-[var(--story-dim)]">
          Voice narration drops here soon — the recording is on its way. The text
          below is the whole story meanwhile.
        </p>
      </div>
    )
  }

  return (
    <div
      data-testid="audio-player"
      className="mt-14 rounded-2xl border border-[var(--story-amber)]/35 bg-[var(--story-amber)]/5 p-5 shadow-[0_0_40px_rgba(232,169,75,0.12)]"
    >
      <div className="flex flex-wrap items-center gap-4">
        <div
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-[var(--story-amber)]/40 text-[var(--story-amber)]"
          data-testid="audio-state-indicator"
        >
          {active ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="5" width="4" height="14" rx="1" />
              <rect x="14" y="5" width="4" height="14" rx="1" />
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5.5v13l11-6.5z" />
            </svg>
          )}
        </div>

        <div className="min-w-[160px] flex-1">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--story-amber)]/80">
            My story, in my voice
          </p>
          <input
            type="range"
            min="0"
            max={duration || 0}
            step="0.1"
            value={time}
            onChange={seek}
            data-testid="audio-progress"
            aria-label="Seek narration"
            className="mt-2 h-1.5 w-full cursor-pointer appearance-none rounded-full bg-[var(--story-amber)]/25 accent-[var(--story-amber)]"
          />
          <div className="font-mono mt-1 flex justify-between text-[10px] text-[var(--story-dim)]">
            <span data-testid="audio-time-elapsed">{fmt(time)}</span>
            <span data-testid="audio-time-total">{fmt(duration)}</span>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={cycleRate}
            data-testid="audio-speed"
            className="font-mono rounded-full border border-[var(--story-amber)]/40 px-3 py-1.5 text-[11px] text-[var(--story-amber)] transition hover:bg-[var(--story-amber)]/10"
          >
            {rate}×
          </button>
          <button
            type="button"
            onClick={() => setShowTranscript((v) => !v)}
            data-testid="audio-transcript-toggle"
            aria-expanded={showTranscript}
            className="font-mono rounded-full border border-[var(--story-amber)]/40 px-3 py-1.5 text-[11px] text-[var(--story-amber)] transition hover:bg-[var(--story-amber)]/10"
          >
            {showTranscript ? 'Hide transcript' : 'Transcript'}
          </button>
        </div>
      </div>

      {showTranscript && (
        <div
          data-testid="audio-transcript"
          className="mt-4 max-h-56 overflow-y-auto border-t border-[var(--story-amber)]/20 pt-4"
        >
          {STORY.paragraphs.map((p, i) => (
            <p key={i} className="font-serif-vintage mb-3 text-sm leading-relaxed text-[var(--story-dim)]">
              {p}
            </p>
          ))}
          <p className="font-serif-vintage text-sm italic text-[var(--story-amber)]">
            {STORY.closing.join(' ')}
          </p>
        </div>
      )}
    </div>
  )
}

export default function Story() {
  return (
    <section
      id="story"
      data-testid="story-section"
      className="relative overflow-hidden bg-[var(--story-bg)] px-6 py-24 text-[var(--story-ink)] md:px-12 md:py-36"
    >
      {/* ambient amber light sweeps */}
      <div
        className="pointer-events-none absolute -left-1/3 top-1/4 h-[140%] w-[70%] -rotate-12 opacity-[0.07]"
        style={{ background: 'linear-gradient(90deg, transparent, var(--story-amber), transparent)' }}
      />
      <div
        className="pointer-events-none absolute -right-1/3 bottom-0 h-[140%] w-[70%] rotate-12 opacity-[0.05]"
        style={{ background: 'linear-gradient(90deg, transparent, var(--story-amber), transparent)' }}
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(232,169,75,0.10), transparent 60%)' }}
      />

      <div className="relative z-10 mx-auto max-w-2xl">
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="font-mono text-center text-[11px] uppercase tracking-[0.3em] text-[var(--story-amber)]/70"
        >
          {STORY.kicker}
        </motion.p>

        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-serif-vintage mt-5 text-center text-3xl italic leading-tight sm:text-4xl md:text-5xl"
        >
          {STORY.heading[0]}
          <br />
          {STORY.heading[1]}
        </motion.h2>

        <div className="mt-14 flex flex-col gap-7">
          {STORY.paragraphs.map((p, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="font-serif-vintage text-lg leading-relaxed text-[var(--story-dim)] md:text-xl"
            >
              {p}
            </motion.p>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="mt-16 border-t border-[var(--story-amber)]/20 pt-10 text-center"
        >
          <p className="font-serif-vintage text-2xl italic text-[var(--story-amber)] sm:text-3xl">
            {STORY.closing[0]}
            <br />
            {STORY.closing[1]}
          </p>
        </motion.div>

        <NarrationPlayer />
      </div>
    </section>
  )
}
